import { Action, AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import API from '@wetransfer/transfer/api';
import ActionHelper from '@wetransfer/transfer/modules/redux-actions';
import {
  generateErrorMessage,
  validationError,
} from '@wetransfer/transfer/store/action/helper/error';
import Notification from '@wetransfer/transfer/store/action/notification';
import Pending from '@wetransfer/transfer/store/action/pending';
import { State } from '@wetransfer/transfer/store/types';

import ImageHelper, {
  CuratedWallPaper,
  UserUploadedAssetModel,
  ASSET_MODEL_CLASS_MAPPING,
  Wallpaper,
} from './helper/image';

export const actions = ActionHelper.types([
  'GET_TIERS_PRICING',
  'GET_SEATS_PRICING',
  'RESET_TIERS_PRICING',
  'RESET_SEATS_PRICING',

  'SET_CREDIT_CARD_BRAND',
  'SET_ACTIVE_PAYMENT_METHOD',
  'SET_PAYMENT_METHOD_TO_COMPLETE',
  'RESET_PAYMENT_METHOD_TO_COMPLETE',

  'SET_UPLOADER_OPTIONS',
  'SET_SHOW_CONSENT_WALL_PREFERENCES',
  'RESET_TIERS_ERRORS',
]) as Record<string, string>;

export default ActionHelper.generate({
  getTiersPricing(params: {
    country_code: string;
    state_code?: string;
    vat_number?: string;
    coupon_code?: string;
  }): ThunkAction<Promise<void>, State, null, Action> {
    const pendingId = 'getTiersPricing';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = (await API.UI.getTiersPricing({
          ...params,
        })) as {
          body: State['ui']['tiersPricing'];
        };

        const action: AnyAction = {
          type: actions.GET_TIERS_PRICING,
          payload: response.body,
        };

        dispatch(action);
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'retrieve_checkout_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  uploadCuratedWallpaper(
    wallpaper: CuratedWallPaper
  ): ThunkAction<Promise<Wallpaper>, State, null, Action> {
    const pendingId = 'uploadAsset_wallpaper';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const { account } = getState();

        const response = await ImageHelper.allocateCuratedWallpaper(
          account.id,
          wallpaper.imagePath
        );
        let wallpaperModel = response.body;

        // Today there are no curated video wallpapers, but if it exists one day
        // we don't need to send it to Peek because they are served directly from S3
        if (wallpaperModel.wallpaper_type !== 'video') {
          await ImageHelper.processPreviews(wallpaperModel);
          // This will enrich the model with preview URLs from Peek
          wallpaperModel = (await ImageHelper.getPreviewUrls(
            wallpaperModel
          )) as Wallpaper;
        }

        dispatch(Pending.resetPending(pendingId));

        return wallpaperModel;
      } catch (error) {
        if (!error.aborted) {
          dispatch(
            Notification.setNotification(
              'error',
              generateErrorMessage(error.status, 'wallpaper')
            )
          );
        }
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  uploadAsset(
    file: File,
    type: keyof typeof ASSET_MODEL_CLASS_MAPPING
  ): ThunkAction<Promise<UserUploadedAssetModel | null>, State, null, Action> {
    const pendingId = 'uploadAsset_' + type;

    return async (dispatch, getState) => {
      const { size, name, type: mimeType } = file;

      const {
        session: { assetUpload },
      } = getState();

      if (!ImageHelper.isFileValid(type, size, mimeType, assetUpload)) {
        dispatch(
          Notification.setNotification('error', validationError(type, mimeType))
        );

        return;
      }
      dispatch(Pending.setPending(pendingId));

      try {
        const { account } = getState();

        const allocatedImage = await ImageHelper.allocateAsset(
          account.id,
          type,
          name
        );
        await ImageHelper.uploadAsset(
          allocatedImage.body.presigned_put_url,
          allocatedImage.body.content_type,
          file
        );

        let model = allocatedImage.body.model;

        // video wallpapers are served direct from S3 and don't need to be processed
        if ((model as Wallpaper).wallpaper_type !== 'video') {
          // We can only post the item to Peek after uploadAsset otherwise,
          // the object will not exist in the bucket
          await ImageHelper.processPreviews(model);
          model = await ImageHelper.getPreviewUrls(model);
        }

        dispatch(Pending.resetPending(pendingId));

        return model;
      } catch (error) {
        if (!error.aborted) {
          dispatch(
            Notification.setNotification(
              'error',
              generateErrorMessage(error.status, type, mimeType)
            )
          );
        }
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  cancelUploadAsset(
    type: string
  ): ThunkAction<Promise<void>, State, null, Action> {
    const pendingId = 'uploadAsset_' + type;

    ImageHelper.cancelUpload();

    return async (dispatch) => {
      dispatch(Pending.resetPending(pendingId));
    };
  },

  resetTiersPricing: [actions.RESET_TIERS_PRICING],
  resetTiersErrors: [actions.RESET_TIERS_ERRORS],

  setActivePaymentMethod: [actions.SET_ACTIVE_PAYMENT_METHOD, 'method'],
  setCreditCardBrand: [actions.SET_CREDIT_CARD_BRAND, 'brand'],
  setPaymentMethodToComplete: [
    actions.SET_PAYMENT_METHOD_TO_COMPLETE,
    'method',
  ],
  resetPaymentMethodToComplete: [
    actions.RESET_PAYMENT_METHOD_TO_COMPLETE,
    'method',
  ],
  setUploaderOptions: [actions.SET_UPLOADER_OPTIONS, 'options'],
  setShowConsentWallPreferences: [
    actions.SET_SHOW_CONSENT_WALL_PREFERENCES,
    'value',
  ],
});
